import Modal from 'components/Modal';
import appointmentApis from 'features/appointment/services/apis';
import { find, flatten, sumBy } from 'lodash';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import shopSelectors from 'services/shop/selectors';
import { ICategoryItemData, IServiceItemData } from 'services/shop/types/categories';
import { formatCurrency } from 'utils/formatCurrency';
import styled, { css } from 'styled-components';

type IModalServicePickerProps = {};
type IModalServicePickerRef = {
  open: (_staffId: string) => Promise<IServiceItemData[]>;
  close: () => void;
};

// Local selection shape: tracks how many of each service the user picked.
type ISelectedServiceItem = {
  serviceId: string;
  catId: string;
  amount: number;
};

export const ModalServicePickerRef = React.createRef<IModalServicePickerRef>();
const ModalServicePicker = forwardRef<IModalServicePickerRef, IModalServicePickerProps>((props, ref) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<ICategoryItemData[]>([]);
  const [open, setOpen] = useState(false);
  const [categorySelected, setCategorySelected] = useState('');
  const [selectedData, setSelectedData] = useState<ISelectedServiceItem[]>([]);
  const callbackResolve = useRef<(services: IServiceItemData[]) => void>(() => undefined);
  const reqStaffRef = useRef('');
  const shopCategories = shopSelectors.data.categories();

  const effectiveCategories = useMemo<ICategoryItemData[]>(
    () => (categories.length ? categories : shopCategories),
    [categories, shopCategories]
  );

  const allServices = useMemo(
    () => flatten(effectiveCategories.map((o) => o.services || [])),
    [effectiveCategories]
  );

  const servicesFilterByCategory = useMemo(
    () => effectiveCategories.find((category) => category.id === categorySelected)?.services || [],
    [effectiveCategories, categorySelected]
  );

  // Keep a valid selected category as the source switches (fetched -> fallback).
  useEffect(() => {
    if (!effectiveCategories.length) return;
    if (!effectiveCategories.some((c) => c.id === categorySelected)) {
      setCategorySelected(effectiveCategories[0].id);
    }
  }, [effectiveCategories, categorySelected]);

  const handleOpen: IModalServicePickerRef['open'] = (_staffId: string) => {
    setOpen(true);
    setSelectedData([]);
    // Reset to the full shop list immediately; the staff fetch overrides it below.
    setCategories([]);
    reqStaffRef.current = _staffId;
    fetchData(_staffId);
    return new Promise((resolve) => {
      callbackResolve.current = resolve;
    });
  };

  const handleClose: IModalServicePickerRef['close'] = () => {
    setOpen(false);
    callbackResolve.current([]);
  };

  const fetchData = async (_staffId: string) => {
    setLoading(true);
    try {
      const resData: IResponseDataBody<ICategoryItemData[]> = await appointmentApis.getCategoriesByStaff(_staffId);
      const data = resData?.data?.data || [];
      if (reqStaffRef.current === _staffId) setCategories(data);
    } catch (error) {
      // Keep the full shop default on failure.
    } finally {
      if (reqStaffRef.current === _staffId) setLoading(false);
    }
  };

  const onAdd = (service: IServiceItemData) => {
    const selectedItem = find(selectedData, (o) => o.serviceId === service.id);
    if (!selectedItem) {
      setSelectedData((prev) => [...prev, { serviceId: service.id, catId: service.catId, amount: 1 }]);
      return;
    }
    setSelectedData((prev) =>
      prev.map((o) => (o.serviceId === service.id ? { ...o, amount: o.amount + 1 } : o))
    );
  };

  const onRemove = (serviceId: string) => {
    setSelectedData((prev) =>
      prev
        .map((o) => (o.serviceId === serviceId ? { ...o, amount: o.amount - 1 } : o))
        .filter((o) => o.amount > 0)
    );
  };

  const onSubmit = () => {
    setOpen(false);
    const result: IServiceItemData[] = [];
    selectedData.forEach((item) => {
      const original = allServices.find((s) => s.id === item.serviceId);
      if (!original) return;
      for (let i = 0; i < item.amount; i++) {
        if (original.childServices?.length) {
          original.childServices.forEach((child) => result.push(child));
        } else {
          result.push(original);
        }
      }
    });
    callbackResolve.current(result);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  return (
    <Modal
      modalTitle='Add Services'
      visible={open}
      onClose={handleClose}
      onSubmit={onSubmit}
      width={'1600px'}
      noneBodyStyle
      noneFooterStyle
      footerPadding={0.5}
      okTitle='OK'
      v2
    >
      <Container>
        <CateGoryContainer>
          {effectiveCategories.map((category) => {
            const count = sumBy(selectedData.filter((o) => o.catId === category.id), 'amount');
            return (
              <CategoryItem
                key={category.id}
                selected={categorySelected === category.id}
                onClick={() => setCategorySelected(category.id)}
              >
                <div className='text'>{category.categoryName} {count ? `(${count})` : ''}</div>
              </CategoryItem>
            );
          })}
        </CateGoryContainer>
        <ServicesContainer>
          {!loading && !servicesFilterByCategory.length && <div className='status'>No services available</div>}
          <div className='list-services'>
            {servicesFilterByCategory.map((service) => {
              const selected = selectedData.find((o) => o.serviceId === service.id);
              return (
                <ServiceItem selected={!!selected} onClick={() => onAdd(service)} key={service.id}>
                  {!!selected && (
                    <div className='icon'>
                      <IcCheck />
                    </div>
                  )}
                  <div className='name'>
                    {service.serviceName} <span className='price'>({formatCurrency(service.priceSell)})</span>
                  </div>
                  <ActionButton>
                    <button type='button' onClick={(e) => [e.stopPropagation(), onAdd(service)]}><span>+</span></button>
                    <div className='amount'>{selected?.amount || 0}</div>
                    <button type='button' onClick={(e) => [e.stopPropagation(), onRemove(service.id)]}><span>-</span></button>
                  </ActionButton>
                </ServiceItem>
              );
            })}
          </div>
        </ServicesContainer>
      </Container>
    </Modal>
  );
});

ModalServicePicker.displayName = 'ModalServicePicker';

export default ModalServicePicker;

const IcCheck = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='27' height='28' viewBox='0 0 27 28' fill='none'>
    <path
      d='M10.9741 20.6667C10.6183 20.6667 10.2804 20.5179 10.0314 20.2575L4.9977 14.9929C4.48189 14.4535 4.48189 13.5606 4.9977 13.0211C5.51351 12.4816 6.36727 12.4816 6.88308 13.0211L10.9741 17.2997L20.1164 7.73798C20.6322 7.19851 21.4859 7.19851 22.0018 7.73798C22.5176 8.27745 22.5176 9.17037 22.0018 9.70984L11.9168 20.2575C11.6678 20.5179 11.3298 20.6667 10.9741 20.6667Z'
      fill='white'
    />
  </svg>
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CateGoryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(175px, 1fr));
  padding: 1rem;
`;

const CategoryItem = styled.div<{ selected: boolean }>`
  display: flex;
  padding: 25px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  color: #505050;
  text-align: center;
  font-family: Poppins;
  background: #fcfcfd;
  box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
    0 -3px 0 0 #d6d6e7 inset;
  cursor: pointer;
  ${({ selected }) =>
    selected &&
    css`
      background: #2d6772;
      box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
        0 -3px 0 0 rgba(8, 56, 65, 0.5) inset;
    `}
  .text {
    font-size: 18px;
    font-weight: 600;
    line-height: 30px;
    text-transform: capitalize;
    ${({ selected }) => selected && css`color: #fff;`}
  }
`;

const ServicesContainer = styled.div`
  height: 700px;
  overflow-y: auto;
  padding: 0 1rem;
  .status {
    padding: 24px 0;
    text-align: center;
    color: #64748b;
    font-family: Poppins;
    font-size: 18px;
  }
  .list-services {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 16px;
    row-gap: 8px;
  }
`;

const ServiceItem = styled.div<{ selected: boolean }>`
  cursor: pointer;
  height: 59px;
  padding: 8px 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border-radius: 5px;
  background: #e5e6eb;
  box-sizing: border-box;
  border: 2px solid transparent;
  box-shadow: 0 -4px 4px 0 rgba(0, 0, 0, 0.25) inset, 0 8px 4px 3px rgba(255, 255, 255, 0.25) inset;
  transition: all 0.3s ease;
  ${({ selected }) => selected && css`border-color: #d2464f;`}

  .name {
    flex: 1;
    overflow: hidden;
    color: #1d2129;
    text-overflow: ellipsis;
    font-size: 18px;
    font-weight: 400;
    line-height: normal;
  }
  .price {
    color: #1d2129;
    font-size: 18px;
    font-weight: 600;
    text-transform: capitalize;
  }
  .icon {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 100px;
    background: #d2464f;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ActionButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  .amount {
    width: 30px;
    text-align: center;
    font-size: 18px;
    font-weight: 600;
  }
  button {
    width: 40px;
    height: 40px;
    border-radius: 5px;
    border: 1px solid #ccd4dc;
    background: #fff;
    span {
      font-size: 24px;
    }
    &:active {
      background: #eee;
    }
  }
`;
